/* eslint-disable import/no-cycle */
import debounce from 'lodash/debounce';
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { DisplayMode } from '@/interface/enum';
import { usePlatformActions } from '@/libraries/composables/usePlatformActions';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useRootStore } from '@/stores/rootStore';

let previousScrollY = 0;
let delayedDispatchMode: ((displayMode: DisplayMode) => void) | null = null;

export const useHeaderDisplayStore = defineStore('headerDisplay', () => {
    const isShowPpHeader = ref(true);
    const isShowSportSwitch = ref(true);
    const isShowMarketSwitch = ref(true);
    const isShowCollapse = ref(true);
    const isShowSportsFooter = ref(true);
    const isShowBackToTopScroll = ref(true);
    const isShowWidgetSwitch = ref(true);
    const isShowGenericHeader = ref(true);

    const { headerDisplayHeight } = usePlatformActions();
    const ppHeight = ref(headerDisplayHeight.value);
    const bannerHeight = ref(0);
    const sportSwitchHeight = ref(0);
    const marketSwitchHeight = ref(0);
    const collapseHeight = ref(0);
    const sportsFooterHeight = ref(0);
    const genericHeaderHeight = ref(0);

    const ppTranslateY = ref(0);
    const sportSwitchTranslateY = ref(0);
    const marketSwitchTranslateY = ref(0);
    const collapseTranslateY = ref(0);
    const genericHeaderTranslateY = ref(0);

    const headerTotalHeight = computed(() => ppHeight.value
            + sportSwitchHeight.value
            + marketSwitchHeight.value
            + collapseHeight.value
            + genericHeaderHeight.value);

    const marketTabsHeight = computed(() => genericHeaderHeight.value + ppHeight.value - 1);

    const ppTop = computed(() => 0);

    const sportSwitchTop = computed(() => ppHeight.value - 1);

    // -2 to prevent 1px gap
    const marketSwitchTop = computed(() => sportSwitchTop.value + sportSwitchHeight.value - 2);

    const collapseTop = computed(() => marketSwitchTop.value + marketSwitchHeight.value);

    const collapseOpacity = computed(() => (collapseTranslateY.value === 0 ? 0 : 0.8));

    const genericHeaderTop = computed(() => ppHeight.value - 1);

    // 10 to prevent iPhoneX bottom region
    const sportsFooterTranslateY = computed(() => (isShowSportsFooter.value ? 0 : sportsFooterHeight.value + 10));

    const backToTopHeight = computed(() => sportsFooterHeight.value + 10);

    const { isShowBetSlip } = storeToRefs(useBetSlipStore());
    function syncScrollY(scrollY = 0) {
        previousScrollY = scrollY;
    }

    function updateScrollY(scrollY = 0) {
        const move = scrollY - previousScrollY;
        syncScrollY(scrollY);

        // ignore when scroll position didn't reach the bottom of header
        if (scrollY < headerTotalHeight.value) {
            setDisplayMode(DisplayMode.ShowAll);
            return;
        }

        delayedDispatchMode ??= debounce((mode: DisplayMode) => setDisplayMode(mode), 500);

        const direction = move > 0 ? 'DOWN' : 'UP';
        if (direction === 'UP') {
            setDisplayMode(DisplayMode.ShowAll);
        } else if (direction === 'DOWN') {
            setDisplayMode(DisplayMode.ScrollDown);
        }

        // special rule
        if (isShowBetSlip.value) {
            delayedDispatchMode(DisplayMode.ShowInfo);
        }
    }

    function setDisplayMode(displayMode: DisplayMode) {
        switch (displayMode) {
            case DisplayMode.ScrollDown:
                setState({
                    isShowPpHeader: false,
                    isShowSportSwitch: false,
                    isShowMarketSwitch: true,
                    isShowCollapse: true,
                    isShowSportsFooter: false,
                    isShowBackToTopScroll: false,
                    isShowWidgetSwitch: false,
                    isShowGenericHeader: true,
                });
                break;
            case DisplayMode.ShowInfo:
                setState({
                    isShowPpHeader: true,
                    isShowSportSwitch: true,
                    isShowMarketSwitch: true,
                    isShowCollapse: true,
                    isShowSportsFooter: true,
                    isShowGenericHeader: true,
                });
                break;
            case DisplayMode.ShowAll:
            default:
                setState({
                    isShowPpHeader: true,
                    isShowSportSwitch: true,
                    isShowMarketSwitch: true,
                    isShowCollapse: true,
                    isShowSportsFooter: true,
                    isShowBackToTopScroll: true,
                    isShowWidgetSwitch: true,
                    isShowGenericHeader: true,
                });
                break;
        }
    }

    const { headerTopOffset } = usePlatformActions();

    function setState(newVal: Record<string, any>) {
        let stateChanged = false;
        const state: Record<string, Ref<boolean | number>> = {
            isShowPpHeader,
            isShowSportSwitch,
            isShowMarketSwitch,
            isShowCollapse,
            isShowSportsFooter,
            isShowBackToTopScroll,
            isShowWidgetSwitch,
            isShowGenericHeader,

            ppHeight,
            bannerHeight,
            sportSwitchHeight,
            marketSwitchHeight,
            collapseHeight,
            sportsFooterHeight,
            genericHeaderHeight,

            ppTranslateY,
            sportSwitchTranslateY,
            marketSwitchTranslateY,
            collapseTranslateY,
            genericHeaderTranslateY,
        };
        Object.keys(newVal).forEach((key) => {
            if (state[key].value !== newVal[key]) {
                state[key].value = newVal[key];
                stateChanged = true;
            }
        });

        const { isMixParlayMode, isWidgetOpened } = storeToRefs(useRootStore());
        const { isShowBetBuilderBetSlip } = storeToRefs(useBetSlipStore());
        // special rule
        if (isMixParlayMode.value || isShowBetBuilderBetSlip.value) {
            if (!isShowSportsFooter.value) stateChanged = true;
            isShowSportsFooter.value = true;
        }
        if (isWidgetOpened.value) {
            if (isShowSportsFooter.value) stateChanged = true;
            isShowSportsFooter.value = false;

            if (!isShowWidgetSwitch.value) stateChanged = true;
            isShowWidgetSwitch.value = true;
        }
        if (!stateChanged) return;

        // calculate translateY
        const elements = [{
            show: isShowPpHeader.value,
            height: ppHeight.value - headerTopOffset.value,
            translateRef: ppTranslateY,
        }, {
            show: isShowSportSwitch.value,
            height: sportSwitchHeight.value - 1,
            translateRef: sportSwitchTranslateY,
        }, {
            show: isShowMarketSwitch.value,
            height: marketSwitchHeight.value,
            translateRef: marketSwitchTranslateY,
        }, {
            show: isShowCollapse.value,
            height: collapseHeight.value,
            translateRef: collapseTranslateY,
        }, {
            show: isShowGenericHeader.value,
            height: genericHeaderHeight.value,
            translateRef: genericHeaderTranslateY,
        }];
        elements.forEach((el, index) => {
            if (el.show) {
                // display element's translateY is the sum of height of the hidden element above current one
                el.translateRef.value = -(elements
                    .slice(0, index)
                    .filter(e => !e.show)
                    .reduce((a, b) => a + b.height, 0)
                );
            } else if (!el.show) {
                // hidden element's translateY is the sum of height of all hidden element
                el.translateRef.value = -(elements
                    .filter(e => !e.show)
                    .reduce((a, b) => a + b.height, 0)
                );
            }
        });
    }

    return {
        isShowPpHeader,
        isShowSportSwitch,
        isShowMarketSwitch,
        isShowCollapse,
        isShowSportsFooter,
        isShowBackToTopScroll,
        isShowWidgetSwitch,
        isShowGenericHeader,

        ppHeight,
        bannerHeight,
        sportSwitchHeight,
        marketSwitchHeight,
        collapseHeight,
        sportsFooterHeight,
        genericHeaderHeight,
        marketTabsHeight,

        ppTranslateY,
        sportSwitchTranslateY,
        marketSwitchTranslateY,
        collapseTranslateY,
        genericHeaderTranslateY,

        headerTotalHeight,
        ppTop,
        sportSwitchTop,
        marketSwitchTop,
        collapseTop,
        collapseOpacity,
        genericHeaderTop,
        sportsFooterTranslateY,
        backToTopHeight,

        syncScrollY,
        updateScrollY,
        setDisplayMode,
        setState,
    };
});
